import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '../../utils/cn';

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium leading-none transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'bg-primary text-primary-foreground shadow hover:bg-[var(--primary-hover)]',
                destructive:
                    'bg-destructive text-destructive-foreground shadow-sm hover:bg-[var(--destructive-hover)] focus-visible:ring-[var(--ring-destructive)]',
                outline:
                    'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
                secondary:
                    'bg-secondary text-secondary-foreground shadow-sm hover:bg-[var(--secondary-hover)]',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 hover:underline',
                assistant:
                    'bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow hover:from-indigo-600 hover:to-violet-600 hover:shadow-md',
            },
            size: {
                default: 'h-9 px-4 py-2',
                sm: 'h-8 rounded-md px-3 text-xs',
                lg: 'h-10 rounded-md px-8',
                icon: 'h-9 w-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

const Button = React.forwardRef(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            leftIcon,
            rightIcon,
            loading = false,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        const isDisabled = disabled || loading;
        const classes = cn(
            buttonVariants({ variant, size }),
            loading && 'shimmer cursor-wait',
            className,
        );

        if (asChild) {
            return (
                <Slot
                    className={classes}
                    ref={ref}
                    disabled={isDisabled}
                    aria-busy={loading || undefined}
                    {...props}
                >
                    {children}
                </Slot>
            );
        }

        return (
            <button
                className={classes}
                ref={ref}
                disabled={isDisabled}
                aria-busy={loading || undefined}
                {...props}
            >
                {leftIcon && (
                    <span className="size-4 shrink-0 flex items-center">
                        {leftIcon}
                    </span>
                )}
                {children}
                {rightIcon && (
                    <span className="size-4 shrink-0 flex items-center">
                        {rightIcon}
                    </span>
                )}
            </button>
        );
    },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
